import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { KsResponse } from '@interfaces/ks-response.interface';
import { SubscriptionInterface } from '@interfaces/subscription-plan.interface';
import { getPictureUrlFromProfile } from '@providers/helpers/helpers';
import { TranslateInitializer } from '@providers/i18n/translate.initializer';
import { nameFromUser } from '@providers/payment-gateways/helpers';
import { PendingRequestService } from '@providers/pending-request/pending.request.service';
import { BehaviorSubject, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, share, switchMap } from 'rxjs/operators';

import {
  AnonymousSessionInterface,
  LoginResponseInterface,
  UserRealTimeInterface,
  UserSessionResponseInterface,
} from '../../interfaces/interfaces';
import { Api } from '../api/api';
import { Config } from '../config/config';
import { CountryService } from '../country/country';
import { DeviceKS } from '../device-ks/device-ks';
import { LocalStorage } from '../local-storage/local-storage';
import { MetricsService } from '../metrics/metrics.service';
import { Notifications } from '../notifications/notifications';
import { SessionService } from './session.service';
import { UserRealtimeService } from './user-realtime';
/**
 * Most apps have the concept of a User. This is a simple provider
 * with stubs for login/signup/etc.
 *
 * This User provider makes calls to our API at the `login` and `signup` endpoints.
 *
 * By default, it expects `login` and `signup` to return a JSON object of the shape:
 *
 * ```json
 * {
 *   status: 'success',
 *   user: {
 *     // User fields your app needs, like 'id', 'name', 'email', etc.
 *   }
 * }
 * ```
 *
 * If the `status` field is not `success`, then an error is detected and returned.
 */
@Injectable()
export class User {
  _user: UserRealTimeInterface;
  public user$: BehaviorSubject<UserRealTimeInterface> = new BehaviorSubject(undefined);
  // sessionDeleteSub: Subscription;
  localSession: any;
  readyState: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  userLoginOK: Subject<any> = new Subject();
  keepAliveInterval;
  sessionError: boolean;
  messages: any;
  goRootSamsung: Subject<any> = new Subject();

  dataSubscriptionEmpty: SubscriptionInterface = {
    card: {
      brand: null,
      last4: null,
      expiration: null,
    },
    subscription: {
      id: null,
      start: null,
      end: null,
      status: null,
      trial_days: null,
      next_payment: null,
    },
    plan: {
      name: null,
      type: null,
      id: null,
      currency: null,
      price_recurrent: null,
      price: null,
    },
  };
  constructor(
    private country: CountryService,
    private translateInitial: TranslateInitializer,
    public config: Config,
    public deviceKS: DeviceKS,
    public http: HttpClient,
    public api: Api,
    public notifications: Notifications,
    private metrics: MetricsService,
    private localStorage: LocalStorage,
    private pendingRequestService: PendingRequestService,
    private userRealtime: UserRealtimeService,
    private sessionService: SessionService
  ) {}

  public static transformFromResponseData(res: any) {
    return {
      success: res.success,
      data: {
        ...res.data.user,
        plans: res.data.plans,
        session_tv: {
          token: res.data.token,
          qr: res.data.qr,
          is_register: res.data.is_register,
          signup: res.data.signup,
        },
      },
    };
  }

  doReload() {
    document.write('');
    location.reload();
  }

  onRetry(e: KeyboardEvent) {
    // eslint-disable-next-line import/no-deprecated
    if (e.keyCode === 13) {
      (window as any).doReload();
      return false;
    }
    document.getElementById('retry-btn').focus();
    return false;
  }

  setViewPayment() {
    this.api
      .post('send/notification/', {
        token: this.getLocalSession().token,
      })
      .subscribe(() => {});
  }
  // Returns true if user is logged in
  get authenticated(): boolean {
    return this._user !== null && this._user !== undefined;
  }

  // Returns current user
  get currentUser(): UserRealTimeInterface {
    return this.authenticated ? this._user : null;
  }

  get premium() {
    return this.currentUser && this.currentUser.premium;
  }

  get isFreemium() {
    return this.authenticated && !this.premium;
  }

  get getUserPhoto() {
    const user = this._user;
    let urlPhoto = './images/app/iconos/profile-panel.png';
    if (user) {
      urlPhoto = user.img
        ? user.img
        : user.fb_id
        ? 'https://graph.facebook.com/' + user.fb_id + '/picture?redirect=true&type=large&height=200&width=200'
        : './images/app/iconos/profile-panel.png';
      this._user.img = urlPhoto;
    }
    return urlPhoto;
  }

  /**
   * Send a POST request to our login endpoint with the data
   * the user entered on the form.
   */
  login(accountInfo: any): Observable<UserSessionResponseInterface> {
    const seq = this.api
      .post('accounts/signin', accountInfo, environment.apiExtraHeaders)
      .pipe(
        switchMap((res: LoginResponseInterface) => {
          // If the API returned a successful response, mark the user as logged in
          if (res.success === true) {
            res.data.created = res.created;
            return of(res.data);
          } else {
            return throwError(res.message ? res : { message: 'Ocurrio un error inesperado LGN' });
          }
        }),
        catchError(this.handleError.bind(this))
      )
      .pipe(
        switchMap((session: UserSessionResponseInterface) => {
          if (!this.authenticated) {
            return from(this._loggedIn(session)).pipe(map(() => session));
          } else {
            return of(session);
          }
        }),
        share()
      );

    return seq;
  }

  loginWithCode(code: number): Observable<UserSessionResponseInterface> {
    const seq = this.api
      .get('session', { codigo: code }, environment.apiExtraHeaders)
      .pipe(
        switchMap((response: KsResponse<UserSessionResponseInterface>) => {
          if (response.success) {
            response.data.created = response['created'];
            return of(response.data);
          } else {
            return throwError(response);
          }
        }),
        catchError(this.handleError.bind(this))
      )
      .pipe(
        switchMap((session: UserSessionResponseInterface) => {
          if (!this.authenticated) {
            return from(this._loggedIn(session)).pipe(map(() => session));
          } else {
            return of(session);
          }
        }),
        share()
      );

    seq.subscribe(
      async (session: UserSessionResponseInterface) => {
        await this.metrics.identify(session, String(session.id));
      },
      () => {}
    );
    return seq;
  }

  /**
   * Send a POST request to our signup endpoint with the data
   * the user entered on the form.
   */
  signup(accountInfo: any): Observable<UserSessionResponseInterface> {
    // flag for pending request
    this.pendingRequestService.pendingSignupRequest$.next(true);
    const seq = this.api
      .post('accounts/signup', accountInfo, environment.apiExtraHeaders)
      .pipe(
        switchMap((res: LoginResponseInterface) => {
          // If the API returned a successful response, mark the user as logged in
          if (res.success === true) {
            res.data.created = res.created;
            return of(res.data);
          } else {
            return throwError(res.message ? res : { message: 'Ocurrio un error inesperado SGNE' });
          }
        }),
        catchError(this.handleError.bind(this))
      )
      .pipe(
        switchMap((session: UserSessionResponseInterface) => {
          if (!this.authenticated) {
            return from(this._loggedIn(session)).pipe(map(() => session));
          } else {
            return of(session);
          }
        }),
        share()
      );

    seq.subscribe(
      async () => {
        this.pendingRequestService.pendingSignupRequest$.next(false);
      },
      err => {
        this.pendingRequestService.pendingSignupRequest$.next(false);
        console.error('ERROR', err);
      }
    );

    return seq;
  }

  recovery(email) {
    const seq = this.api
      .post('password/reset', email)
      .pipe(
        map(
          res =>
            // If the API returned a successful response, mark the user as logged in
            res
        )
      )
      .pipe(share());

    return seq;
  }

  updateUserProperty(property: string, data: any) {
    this._user[property] = data;
  }

  async updateToPremium() {
    if (this.authenticated) {
      this._user.premium = true;
      this.user$.next(this._user);
      await this.metrics.identify(this._user, String(this._user.id));
      console.log('this.metrics.identify');
      this.metrics.register({ Membership: 'Premium' });
    }
  }

  registerInitialDataOnMetrics() {
    this.deviceKS.registerDeviceDataOnMetrics();
    this.sessionService.registerSessionDataOnMetrics();
    this.country.registerCountryDataOnMetrics();
    this.translateInitial.registerLanguageOnMetrics();
  }

  /**
   * Log the user out, which forgets the session
   */
  async logout() {
    const oldToken = await this.localStorage.getItem('tokenTv');
    return this.sessionService
      .logoutSession(this.deviceKS.currentDUID, oldToken)
      .toPromise()
      .then(session => {
        this.clearUserData();
        this.localSession = session;
      })
      .catch(() => {
        this.clearUserData();
        location.reload();
      });
  }

  clearUserData() {
    this._user = null;
    this.localSession = null;
    this.localStorage.removeItem('tokenUser');
    this.localStorage.removeItem('selectedGenres');
    this.localStorage.removeItem('country');
    this.user$.next(undefined);
    this.metrics.reset();
    this._user = null;
    this.registerInitialDataOnMetrics();
  }

  async initUser(session: UserSessionResponseInterface | AnonymousSessionInterface, whereSession = 'init') {
    console.log('USER : initUser()');
    if (session.is_anonymous) {
      session = session as AnonymousSessionInterface;
      this.localSession = session;
      this._user = null;
    } else {
      session = session as UserSessionResponseInterface;
      this.localSession = session.session_tv;

      // posible error app no abre
      if (!session.first_name) {
        session.first_name = nameFromUser(session);
      }

      if (session.session_tv.is_register && whereSession !== 'init') {
        this.metrics.sendEvent('createAccount', {
          idUser: session.id,
          Premium: !!session.premium,
          Email: session.email,
          'Kanto Email': session.email,
          'Where Session': whereSession,
        });
      }

      this.metrics.sendEvent('openSesion', {
        idUser: session.id,
        Premium: !!session.premium,
        Email: session.email,
        'Where Session': whereSession,
      });
      await this._loggedIn(session);
    }
    this.readyState.next(true);
  }
  async reinitSession() {
    this.sessionService.getNewSession(this.deviceKS.currentDUID).subscribe(
      session => {
        this.initUser(session as UserSessionResponseInterface);
      },
      catchError(err => throwError(err))
    );
  }

  async updateUserWithRealTimeData(data: UserSessionResponseInterface): Promise<UserRealTimeInterface> {
    return this.userRealtime.initRealTimeUser(data);
  }

  /**
   * Process a login/signup response to store user data
   */
  async _loggedIn(session: UserSessionResponseInterface) {
    const userRealtimeData = await this.updateUserWithRealTimeData(session);
    console.log('USER : _loggedIn()', 'session', session, 'userRealtimeData', userRealtimeData);
    this._user = { ...userRealtimeData, ...session };
    if (session.country && this.country.currentCountry !== session.country) {
      this.country.setCountry(session.country);
    }
    this._user.img = this.patchImage(this._user);
    if (this._user.session_tv) {
      this.localSession = this._user.session_tv;
      await this.localStorage.setItem('tokenTv', this._user.session_tv.token);
    }
    await this.localStorage.setItem('tokenUser', this._user.token);
    this.metrics.register({
      'Kanto Email': this._user.email,
      'Kanto User Country': this._user.country,
      Membership: this._user.premium ? 'Premium' : 'Free',
    });
    this.user$.next(this._user);
    this.callKeepalive(this.sessionService.getToken());
  }

  patchImage(user: UserSessionResponseInterface) {
    return getPictureUrlFromProfile(user);
  }
  requestKeepalive(token: string) {
    return this.api.post('sessions/keepalive', { token });
  }

  callKeepalive(token: string) {
    const requestCall = () => {
      const requestCallApi = this.requestKeepalive(token);
      const actions = () => {
        if (this.deviceKS && this.deviceKS.name === 'tizen') {
          this.logout().then(() => {
            this.goRootSamsung.next(true);
          });
        } else {
          location.reload();
        }
      };
      requestCallApi.subscribe(
        response => {
          if (response.success === false) {
            actions();
          }
        },
        () => {
          actions();
        }
      );
    };

    clearInterval(this.keepAliveInterval);
    this.keepAliveInterval = setInterval(requestCall, 1800000);
  }

  async selectPlanForPurchase(plan) {
    return this.sessionService.updateSession({
      selectedPlanForPurchase: plan,
    });
  }

  getLocalSession() {
    return this.localSession;
  }

  /**
   * @deprecated
   * Use SessionService.getQR instead
   */
  getSessionQR() {
    if (!this._user && !this.localSession) {
      // this.router.navigate(['/login']);
      return null;
    }
    return this.sessionService.getQR();
  }

  onReady(callBack: () => any) {
    return this.readyState.subscribe(ready => {
      if (ready) {
        callBack();
      }
    });
  }
  setDataPaymentUser(data: SubscriptionInterface) {
    this.currentUser.payment = data;
    if (this.currentUser.payment) {
      this.currentUser.payment.subscription['end'] = new Date(this.currentUser.payment?.subscription?.end);
      this.currentUser.payment.subscription['start'] = new Date(this.currentUser.payment?.subscription.start);
    }
  }

  setPlans(plans) {
    if (this._user) {
      this._user.plans = plans;
    }
  }

  changePass(data) {
    return this.api.post('accounts/' + this.currentUser.id + '/password/change', data);
  }
  sendEmailToReset(data) {
    return this.api.post('password/reset', data);
  }
  getInfoComplety() {
    return this.api.get('subscription');
  }

  getInfoProfile(): Observable<UserSessionResponseInterface> {
    return this.api.get('user_profile').pipe(
      switchMap(res => {
        if (res.success) {
          return of(res.data as UserSessionResponseInterface);
        } else {
          return throwError(res.message);
        }
      })
    );
  }

  updateUserData() {
    const req = this.getInfoComplety().pipe(
      switchMap((res: KsResponse<any>) => {
        if (res.success) {
          this.setDataPaymentUser(res.data);
          return of(res.data);
        } else if (!res.success) {
          this.setDataPaymentUser(this.dataSubscriptionEmpty);
          return of(this.dataSubscriptionEmpty);
        } else {
          return throwError(res);
        }
      }),
      share()
    );
    return req;
  }

  handleError(err) {
    const error = err.error || err || {};
    error.message = error.message || 'Error desconocido';
    return throwError(error);
  }
}
